import "reflect-metadata";
import {EntityType} from "../entity";
import {statisticsType} from "./statistics";
import {calculateAverage} from "../../util/math-util";
import {TimeStatistics, TimeStatisticsWrapper} from "./time-statistics";
import {transportCategory, TransportCategory} from "../transport-category";
import {StatisticsGrouping, StatisticsGroupingWrapper} from "./statistics-grouping";
import {Exclude, Expose} from "class-transformer";
import {transportMethod, TransportMethod} from "../transport-method";
import {addTime, getTimeFrameDates, TimeFrame} from "../../date/time-frame";
import {adjustToTimeZone, isBefore} from "../../../index";

export interface StatisticsBundle {
  regionStats: StatisticsGroup[]
  pipelineStats: StatisticsGroup[]
  truckCompanyStats: StatisticsGroup[]
  disposalStats: StatisticsGroup[]
  containerStats: StatisticsGroup[]
  operatorStats: StatisticsGroup[]
  siteStats: StatisticsGroup[]
  pickupSiteStats: StatisticsGroup[]
  deliverySiteStats: StatisticsGroup[]
  routeStats: StatisticsGroup[]
  pickupRouteStats: StatisticsGroup[]
  deliveryRouteStats: StatisticsGroup[]
  routeGroupStats: StatisticsGroup[]
  pickupRouteGroupStats: StatisticsGroup[]
  deliveryRouteGroupStats: StatisticsGroup[]
  wellStats: StatisticsGroup[]
}

@Exclude()
export class StatisticsBundleWrapper {
  @Expose()
  pipelineStats: StatisticsGroupWrapper[] = [];

  @Expose()
  truckCompanyStats: StatisticsGroupWrapper[] = [];

  @Expose()
  disposalStats: StatisticsGroupWrapper[] = [];

  @Expose()
  regionStats: StatisticsGroupWrapper[] = [];

  @Expose()
  containerStats: StatisticsGroupWrapper[] = [];

  @Expose()
  operatorStats: StatisticsGroupWrapper[] = [];

  @Expose()
  siteStats: StatisticsGroupWrapper[] = [];

  @Expose()
  pickupSiteStats: StatisticsGroupWrapper[] = [];

  @Expose()
  deliverySiteStats: StatisticsGroupWrapper[] = [];

  @Expose()
  routeStats: StatisticsGroupWrapper[] = [];

  @Expose()
  pickupRouteStats: StatisticsGroupWrapper[] = [];

  @Expose()
  deliveryRouteStats: StatisticsGroupWrapper[] = [];

  @Expose()
  routeGroupStats: StatisticsGroupWrapper[] = [];

  @Expose()
  pickupRouteGroupStats: StatisticsGroupWrapper[] = [];

  @Expose()
  deliveryRouteGroupStats: StatisticsGroupWrapper[] = [];

  @Expose()
  wellStats: StatisticsGroupWrapper[] = [];


  getPipelineStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.pipelineStats)
  }

  getOperatorStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.operatorStats)
  }

  getTruckCompanyStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.truckCompanyStats)
  }

  getDisposalStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.disposalStats)
  }

  getRegionStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.regionStats)
  }

  getContainerStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.containerStats)
  }

  getSiteStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.siteStats)
  }

  getPickupSiteStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.pickupSiteStats)
  }

  getDeliverySiteStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.deliverySiteStats)
  }

  getRouteStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.routeStats)
  }

  getPickupRouteStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.pickupRouteStats)
  }

  getDeliveryRouteStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.deliveryRouteStats)
  }

  getRouteGroupStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.routeGroupStats)
  }

  getPickupRouteGroupStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.pickupRouteGroupStats)
  }

  getDeliveryRouteGroupStats = (id: string): StatisticsGroupWrapper | undefined => {
    return this.findStats(id, this.deliveryRouteGroupStats)
  }

  private findStats = (id: string, statsArray: StatisticsGroupWrapper[]): StatisticsGroupWrapper | undefined => {
    const groupIndex = statsArray.findIndex(g => g.id === id)
    return groupIndex >= 0 ? statsArray[groupIndex] : undefined
  }
}

export interface StatisticsGroup {
  id: string
  name: string
  entityType?: EntityType
  hour: Record<number, TimeStatistics>
  day: Record<number, TimeStatistics>
  week: Record<number, TimeStatistics>
  month: Record<number, TimeStatistics>
  isPickup: boolean
  childMap: Record<string, StatisticsGroup>
  siteSourcesSiteMap: Record<string, StatisticsGroup>
  siteDestinationsMap: Record<string, StatisticsGroup>
  freightTypeMap: Record<string, StatisticsGroup>
  reverseSiteStatMap: Record<string, StatisticsGroup>
  uom: string
  timeUom: string
}

@Exclude()
export class StatisticsGroupWrapper extends StatisticsGroupingWrapper implements StatisticsGrouping {
  @Expose()
  id: string;

  @Expose()
  name: string;

  @Expose()
  entityType?: EntityType;

  @Expose()
  isPickup = false;

  @Expose()
  uom = 'barrels';

  @Expose()
  timeUom = 'hours'

  @Expose()
  hour: Record<number, TimeStatisticsWrapper> = {}

  @Expose()
  day: Record<number, TimeStatisticsWrapper> = {}

  @Expose()
  week: Record<number, TimeStatisticsWrapper> = {}

  @Expose()
  month: Record<number, TimeStatisticsWrapper> = {}

  @Expose()
  childMap: Record<string, StatisticsGroupWrapper> = {}

  @Expose()
  siteSourcesSiteMap: Record<string, StatisticsGroupWrapper> = {};

  @Expose()
  siteDestinationsMap: Record<string, StatisticsGroupWrapper> = {};

  @Expose()
  freightTypeMap: Record<string, StatisticsGroupWrapper> = {};

  @Expose()
  reverseSiteStatMap: Record<string, StatisticsGroupWrapper> = {};

  @Expose()
  siteMap: Record<string, StatisticsGroupWrapper> = {};

  @Expose()
  truckCompanyMap: Record<string, StatisticsGroupWrapper> = {}

  @Expose()
  pipelineMap: Record<string, StatisticsGroupWrapper> = {}

  constructor(id: string, name: string, entityType: EntityType | undefined = undefined) {
    super()

    this.id = id
    this.name = name;
    this.entityType = entityType;
  }

  getRecordCount = (method: TransportMethod | undefined, category: TransportCategory | undefined) : number => {
    let pipelineCount = 0
    let truckingCount = 0

    if (!method) {
      if (!category) {
        pipelineCount += this.pipeline.oil.recordCount
        pipelineCount += this.pipeline.water.recordCount
        truckingCount += this.trucking.oil.recordCount
        truckingCount += this.trucking.water.recordCount
      } else if (category === transportCategory.oil) {
        pipelineCount += this.pipeline.oil.recordCount
        truckingCount += this.trucking.oil.recordCount
      } else if (category === transportCategory.water) {
        pipelineCount += this.pipeline.water.recordCount
        truckingCount += this.trucking.water.recordCount
      }
    } else if (method === transportMethod.pipeline) {
      if (!category) {
        pipelineCount += this.pipeline.oil.recordCount
        pipelineCount += this.pipeline.water.recordCount
      } else if (category === transportCategory.oil) {
        pipelineCount += this.pipeline.oil.recordCount
      } else if (category === transportCategory.water) {
        pipelineCount += this.pipeline.water.recordCount
      }
    } else if (method === transportMethod.trucking) {
      if (!category) {
        truckingCount += this.trucking.oil.recordCount
        truckingCount += this.trucking.water.recordCount
      } else if (category === transportCategory.oil) {
        truckingCount += this.trucking.oil.recordCount
      } else if (category === transportCategory.water) {
        truckingCount += this.trucking.water.recordCount
      }
    }

    return pipelineCount + truckingCount
  }

  getTotalVolumeInventory = (category: TransportCategory | undefined = undefined): number => {
    return this.inventory.getTotalVolume(category);
  };

  getAverageQty = (timeFrame: TimeFrame, fromDttm: number, toDttm: number, method: TransportMethod | undefined = undefined, category: TransportCategory | undefined = undefined): number | undefined => {
    const timeFrameDates = getTimeFrameDates(timeFrame, fromDttm, toDttm)
    let startDttm = timeFrameDates.startDttm
    const endDttm = timeFrameDates.endDttm

    const frameValues: Record<number, number> = {}

    while (isBefore(startDttm, endDttm)) {
      const timeStatsRecord = getTimeStatisticsRecord(timeFrame, this)

      const dttm = adjustToTimeZone(startDttm).getTime()
      if (dttm in timeStatsRecord) {
        const timeStats = timeStatsRecord[adjustToTimeZone(startDttm).getTime()]

        let qty = 0

        if (!method || method === transportMethod.trucking) {
          qty += timeStats.trucking.getValue(statisticsType.quantity, undefined, category)
        }

        if (!method || method === transportMethod.pipeline) {
          qty += timeStats.pipeline.getValue(statisticsType.quantity, undefined, category)
        }

        frameValues[dttm] = qty
      } else {
        frameValues[dttm] = 0
      }

      startDttm = addTime(timeFrame, startDttm, 1)
    }

    const values = Object.values(frameValues)
    const total = values.reduce((prev, curr) => prev + curr, 0)
    return calculateAverage(total, values.length)
  }

  getRateModels(category: TransportCategory | undefined = undefined): string[] {
    const rateModels = new Set<string>();

    for (const rateModel of this.trucking.getRateModels(category)) {
      rateModels.add(rateModel);
    }

    for (const rateModel of this.pipeline.getRateModels(category)) {
      rateModels.add(rateModel);
    }

    return Array.from(rateModels).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
  }
}

export const getTimeStatisticsRecord = (timeFrame: TimeFrame, statistics: StatisticsGroupWrapper): Record<number, TimeStatisticsWrapper> => {
  if (timeFrame === 'HOUR') {
    return statistics.hour
  } else if (timeFrame === "DAY") {
    return statistics.day
  } else if (timeFrame === "WEEK") {
    return statistics.week
  } else {
    return statistics.month
  }
}
